import { useEffect, useState } from 'react'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import { useDispatch } from 'react-redux'
import { formatResponse, formatErrorMessage } from 'utils/formatting'
import { NotificationList } from 'components/common/NotificationList'
import { InputContainer } from 'components/ideal-core-ui'
import { Button } from 'reactstrap'
import s from './AddOptions.scss'
import cn from 'classnames'
import { FaPencilAlt, FaTrashAlt } from 'react-icons/fa'
import { EditItemModal } from './EditItemModal'
import update from 'immutability-helper'
import { withSweetAlert } from 'hocs'
import { useAppBasedApi } from 'hooks'

const AddOptions = ({
  url,
  editUrl,
  toItemsSerializer,
  toItemSerializer,
  texts,
  cy,
  addItem: addItemProp,
  editItem: editItemProp,
  deleteItem: deleteItemProp,
  items: itemsProp,
  styles,
  validation,
  allowEmpty = false,
  itemsType = 'opportunities',
  app
}) => {
  const reduxDispatch = useDispatch()
  const [itemName, setItemName] = useState('')
  const [editedItem, setEditedItem] = useState(null)
  const [items, setItems] = useState([])
  const [isDisabled, setIsDisabled] = useState(validation ? true : false)
  const [isEditItemModalVisible, setIsEditItemModalVisible] = useState(false)
  const requests = useAppBasedApi(app)

  useEffect(() => {
    ;(async () => {
      if (!url) return
      const items = await requests.getRequest(url)
      setItems(toItemsSerializer(items))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!allowEmpty && !itemsProp?.length) return
    setItems(itemsProp)
  }, [allowEmpty, itemsProp])

  useEffect(() => {
    if (!validation) return
    if (validation(itemName)) {
      setIsDisabled(false)
    } else {
      setIsDisabled(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemName])

  const toggleEditItemModal = () => {
    setIsEditItemModalVisible(!isEditItemModalVisible)
  }

  const addItem = async () => {
    try {
      const item = await requests.postRequest(url, {
        name: itemName
      })
      setItems([...items, toItemSerializer ? toItemSerializer(item) : item])
    } catch (error) {
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was a problem adding option',
          message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
          level: 'error'
        }
      })
    }
  }

  const deleteItem = async item => {
    const content = (
      <div>
        <p style={{ color: 'red', textAlign: 'center' }}>This action can&#39;t be reverted</p>
        <p>
          All {itemsType} that use this value will have this field reset to
          &quot;Uncategorized&quot;
        </p>
      </div>
    )
    const sweetAlertFactory = withSweetAlert(content)
    sweetAlertFactory(
      {
        title: 'Are you sure you want to remove this value from your settings?',
        buttons: ['No', 'Yes'],
        icon: 'warning'
      },
      async () => {
        try {
          await requests.deleteRequest(`${url}/${item.value}`)
          const index = items.findIndex(c => c.value === item.value)
          const tmp = [...items]
          tmp.splice(index, 1)
          setItems(tmp)
        } catch (error) {
          reduxDispatch({
            type: PUSH_NOTIFICATION,
            payload: {
              title: 'There was a problem deleting option',
              message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
              level: 'error'
            }
          })
        }
      },
      { cancelConfirmation: false }
    )
  }

  const editItem = async name => {
    try {
      await requests.putRequest(`${editUrl || url}/${editedItem.value}`, {
        name,
        id: editedItem.value
      })
      const index = items.findIndex(c => c.value === editedItem.value)
      const newState = update(items, {
        [index]: {
          label: { $set: name }
        }
      })
      setItems(newState)
    } catch (error) {
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was a problem editing option',
          message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
          level: 'error'
        }
      })
    }
  }

  return (
    <>
      <div style={styles} className={s.container} data-cy={cy}>
        {texts && (
          <>
            {texts.title && <h5>{texts.title}</h5>}
            {texts.description && <p>{texts.description}</p>}
            {texts.subtitle && <h6 className={s.subTitle}>{texts.subtitle}</h6>}
          </>
        )}
        <div className={s.itemWrapper}>
          <InputContainer
            type="text"
            placeholder={texts?.placeholder || 'Enter New Name'}
            cb={value => setItemName(value)}
            text={itemName || ''}
            classname={s.addItemInput}
          />
          <Button
            disabled={isDisabled}
            className={s.greenBtn}
            onClick={() => {
              if (!items.map(i => (toItemSerializer ? i.label : i)).includes(itemName)) {
                addItemProp ? addItemProp(items, itemName) : addItem()
              }
              setItemName('')
            }}
          >
            Add
          </Button>
        </div>
        {items.length > 0 && (
          <ul className={s.itemsWrapper}>
            {items.map(item => (
              <li
                key={toItemSerializer ? item.value : item}
                className={cn({
                  [s.deletable]: !item.system,
                  [s.notDeletable]: item.system
                })}
              >
                <span className={s.item}>{toItemSerializer ? item.label : item}</span>
                {!item.system && (
                  <>
                    <span
                      className={s.removeItem}
                      onClick={() => {
                        deleteItemProp ? deleteItemProp(item, items) : deleteItem(item)
                      }}
                    >
                      <FaTrashAlt />
                    </span>
                    <span
                      className={s.removeItem}
                      onClick={() => {
                        setEditedItem(item)
                        setIsEditItemModalVisible(true)
                      }}
                    >
                      <FaPencilAlt />
                    </span>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      {isEditItemModalVisible && (
        <EditItemModal
          toggleEditItemModal={toggleEditItemModal}
          isEditItemModalVisible={isEditItemModalVisible}
          updateItem={name => editItemProp?.(editedItem.value, name) ?? editItem(name)}
          editedItem={editedItem}
        />
      )}
    </>
  )
}

export default AddOptions
